import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import type { LocalMembership } from '../../services/persistence/identity-store';

interface GroupViewSelectorProps {
  memberships: LocalMembership[];
  selectedGroupId: string;
  active: boolean;
  onAddGroup: () => void;
  onOpenGroup: () => void;
  onSelectGroup: (groupId: string) => void;
}

export function GroupViewSelector({
  memberships,
  selectedGroupId,
  active,
  onAddGroup,
  onOpenGroup,
  onSelectGroup,
}: GroupViewSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const blurCloseTimerRef = useRef<number | null>(null);
  const selected =
    memberships.find((membership) => membership.groupId === selectedGroupId) ?? memberships[0];

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeFromOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeFromEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeFromOutside);
    document.addEventListener('keydown', closeFromEscape);
    return () => {
      document.removeEventListener('mousedown', closeFromOutside);
      document.removeEventListener('keydown', closeFromEscape);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    }
  }, [open]);

  useEffect(
    () => () => {
      if (blurCloseTimerRef.current !== null) {
        window.clearTimeout(blurCloseTimerRef.current);
      }
    },
    [],
  );

  function cancelDeferredBlurClose() {
    if (blurCloseTimerRef.current !== null) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
  }

  function closeAfterFocusSettles() {
    cancelDeferredBlurClose();
    blurCloseTimerRef.current = window.setTimeout(() => {
      blurCloseTimerRef.current = null;
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false);
      }
    }, 0);
  }

  function moveMenuFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
    const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    if (buttons.length === 0) return;
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const targetIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : event.key === 'ArrowDown'
            ? (currentIndex + 1) % buttons.length
            : event.key === 'ArrowUp'
              ? (currentIndex - 1 + buttons.length) % buttons.length
              : -1;
    if (targetIndex >= 0) {
      event.preventDefault();
      buttons[targetIndex]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      toggleRef.current?.focus();
    }
  }

  if (selected === undefined) {
    return (
      <button type="button" role="tab" aria-selected="false" disabled>
        Gruppe
      </button>
    );
  }

  return (
    <div
      className="group-view-selector"
      ref={containerRef}
      onBlur={closeAfterFocusSettles}
      onFocus={cancelDeferredBlurClose}
    >
      <button
        id="group-tab"
        className="group-view-selector__tab"
        type="button"
        role="tab"
        aria-label={`Gruppe ${selected.groupName ?? 'ohne Namen'}`}
        aria-selected={active}
        aria-controls="group-panel"
        onClick={onOpenGroup}
      >
        <span>Gruppe</span>
        <small>{selected.groupName ?? 'Ohne Namen'}</small>
      </button>
      <button
        ref={toggleRef}
        className="group-view-selector__toggle"
        type="button"
        aria-label="Gruppe für den Verlauf auswählen"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true">⌄</span>
      </button>
      {open ? (
        <div
          className="group-view-selector__menu"
          ref={menuRef}
          role="menu"
          aria-label="Gruppenverlauf"
          onKeyDown={moveMenuFocus}
        >
          {memberships.map((membership) => {
            const selectedMembership = membership.groupId === selected.groupId;
            return (
              <button
                key={membership.id}
                type="button"
                role="menuitemradio"
                aria-label={`${membership.groupName ?? 'Gruppe ohne Namen'} · Als ${membership.displayName}`}
                aria-checked={selectedMembership}
                onClick={() => {
                  onSelectGroup(membership.groupId);
                  onOpenGroup();
                  setOpen(false);
                }}
              >
                <span>
                  <strong>{membership.groupName ?? 'Gruppe ohne Namen'}</strong>
                  <small>Als {membership.displayName}</small>
                </span>
                {selectedMembership ? <span aria-hidden="true">✓</span> : null}
              </button>
            );
          })}
          <button
            className="group-view-selector__add"
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAddGroup();
            }}
          >
            <span aria-hidden="true">＋</span>
            <strong>Neuer Gruppe beitreten</strong>
          </button>
        </div>
      ) : null}
    </div>
  );
}
